import React from "react";
import { useRecentSubmissionsQuery } from "../api/submissionApi";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { ExternalLink, Clock, FileText, Search } from "lucide-react";
import { useAuth } from "../App";

type RecentSubmission = {
  submittedAt: string;
  checkPointTitle: string;
  user: { id: string; name: string; email: string; profilePicture?: string };
  project: {
    id: string;
    title: string;
    course: { id: string; name: string; code?: string };
  };
};

function timeAgo(iso: string) {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

function SubmissionRow({ item }: { item: RecentSubmission }) {
  const { user } = useAuth();
  const initials =
    item.user?.name
      ?.split(" ")
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("") || "U";
  const isSelf = item?.user?.id && user?.id && item.user.id === user.id;
  return (
    <div className="flex items-start gap-3 py-3">
      <Avatar className="h-10 w-10 text-white">
        {item.user?.profilePicture ? (
          <AvatarImage src={item.user.profilePicture} alt={item.user.name} />
        ) : (
          <AvatarFallback
            className={
              isSelf ? "bg-blue-500 text-white" : "bg-bits-orange text-white"
            }
          >
            {initials}
          </AvatarFallback>
        )}
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium leading-tight truncate">
            {isSelf ? "You" : item.user?.name || "User"} submitted{" "}
            {item.checkPointTitle}
          </p>
          <span className="shrink-0 text-xs text-muted-foreground">
            {timeAgo(item.submittedAt)}
          </span>
        </div>

        <div className="flex mt-1 text-xs text-muted-foreground truncate">
          <FileText className="h-4 w-4 mr-2" />
          {item.project?.title}
        </div>
      </div>
    </div>
  );
}

export default function RecentSubmissionCard() {
  const { data, isLoading, isError } = useRecentSubmissionsQuery();

  const items = Array.isArray((data as any)?.data?.submission)
    ? ((data as any).data.submission as any[])
    : [];

  const [query, setQuery] = React.useState("");
  const [submittedBy, setSubmittedBy] = React.useState<string>("");

  // Add "All" as the first option in your submitterOptions memo
  const submitterOptions = React.useMemo(
    () => [
      "All", // Add this as first option
      ...Array.from(
        new Set(
          items
            .map((it: any) => it?.user?.name)
            .filter((v: any): v is string => Boolean(v)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    ],
    [items],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it: any) => {
      const matchesText =
        !q ||
        it?.project?.title?.toLowerCase().includes(q) ||
        it?.checkPointTitle?.toLowerCase().includes(q);
      const matchesUser =
        !submittedBy || submittedBy === "All" || it?.user?.name === submittedBy;
      return matchesText && matchesUser;
    });
  }, [items, query, submittedBy]);

  const top3 = items.slice(0, 3);

  return (
    <Card className="border border-border rounded-lg gap-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-bits-warning" />
            Recent Submissions
          </CardTitle>

          <Dialog>
            <DialogTrigger asChild>
              <button className="hover:underline border whitespace-nowrap px-2 text-lg font-semibold rounded-md flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                View All
              </button>
            </DialogTrigger>

            <DialogContent className="max-w-4xl p-0 bg-white overflow-hidden ">
              <DialogHeader className="px-6 pt-6 pb-2">
                <DialogTitle>Recent Submissions</DialogTitle>
              </DialogHeader>

              {/* Controls row */}
              <div className="px-6 pb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by project or checkpoint..."
                      className="pl-9"
                    />
                  </div>

                  <div className="min-w-[220px]">
                    <Select value={submittedBy} onValueChange={setSubmittedBy}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Submitted by" />
                      </SelectTrigger>
                      <SelectContent>
                        {submitterOptions.map((name) => (
                          <SelectItem key={name} value={name}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <ScrollArea className="max-h-[70vh] px-6 pb-4 overflow-auto border-none">
                {isLoading && (
                  <div className="text-sm text-muted-foreground py-4">
                    Loading…
                  </div>
                )}
                {isError && (
                  <div className="text-sm text-destructive py-4">
                    Failed to load submissions.
                  </div>
                )}
                {!isLoading && !isError && filtered.length === 0 && (
                  <div className="text-sm text-muted-foreground py-4">
                    No submissions match your filters.
                  </div>
                )}
                {!isLoading && !isError && filtered.length > 0 && (
                  <div className="space-y-2">
                    {filtered.map((item, idx) => (
                      <div
                        key={idx}
                        className="py-1 px-2 bg-background rounded-md"
                      >
                        <SubmissionRow item={item} />
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent className="pt-0 ">
        {isLoading && (
          <div className="text-sm text-muted-foreground py-3">Loading…</div>
        )}
        {isError && (
          <div className="text-sm text-destructive py-3">
            Failed to load submissions.
          </div>
        )}
        {!isLoading && !isError && top3.length === 0 && (
          <div className="text-sm text-muted-foreground py-3">
            No recent submissions.
          </div>
        )}
        {!isLoading && !isError && top3.length > 0 && (
          <div className="space-y-2">
            {top3.map((item, idx) => (
              <div key={idx} className="py-1 px-2 bg-background rounded-md">
                <SubmissionRow item={item} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
